/**
 * Coordinate Grabber - External JavaScript Widget
 * This script provides coordinate extraction functionality as an external widget
 * Configuration via global window.coordinateGrabberConfig object
 */

(function() {
  'use strict';

  // Configuration from global object or defaults
  const config = window.coordinateGrabberConfig || {};
  const mapboxToken = config.mapboxToken || 'pk.eyJ1Ijoibm93aGVyZWZhc3RnZWFyIiwiYSI6ImNtZTExcGRzcjA5c3cyaXNpYXAxZ3h2dTkifQ.lEKxRk7C5klME3nTfUPviA';
  const containerId = config.containerId || 'area-widget';
  
  // Validate backend URL to prevent manipulation
  function validateBackendUrl(url) {
    if (!url || typeof url !== 'string') {
      return 'https://thelandmark-api-preview.fly.dev';
    }
    
    try {
      const parsedUrl = new URL(url);
      
      // Only allow HTTPS URLs
      if (parsedUrl.protocol !== 'https:') {
        console.warn('[Security] Backend URL must use HTTPS protocol');
        return 'https://thelandmark-api-preview.fly.dev';
      }
      
      // Allow only trusted domains
      const allowedDomains = [
        'thelandmark-api-preview.fly.dev',
        'localhost' // For development
      ];
      
      const hostname = parsedUrl.hostname;
      const isAllowed = allowedDomains.some(domain => 
        hostname === domain || hostname.endsWith('.' + domain)
      );
      
      if (!isAllowed) {
        console.warn('[Security] Backend URL domain not in allowlist:', hostname);
        return 'https://thelandmark-api-preview.fly.dev';
      }
      
      return url;
    } catch (error) {
      console.warn('[Security] Invalid backend URL format:', url);
      return 'https://thelandmark-api-preview.fly.dev';
    }
  }
  
  const backendUrl = validateBackendUrl(config.backendUrl);
  const freeMockupVariantId = config.freeMockupVariantId || null;
  


  // Size validation limits - easily editable for future changes
  const VALIDATION_CONFIG = {
    horizontalMinSize: 500,      // 500m minimum
    horizontalMaxSize: 150000,   // 150km maximum
    validColor: '#FFFFFF',       // Valid rectangle color
    invalidColor: '#FF0000',     // Invalid rectangle color (red)
    tooSmallMessage: 'Too small an area selected, zoom out',
    tooLargeMessage: 'Too large an area selected, zoom in'
  };

  // GLB Viewer provided via Shopify/js/glb-viewer.js
  const GLBViewer = (window.Landmark && window.Landmark.GLBViewer) || null;
  const ShopifyIntegration = (window.Landmark && window.Landmark.ShopifyIntegration) || null;
  const MapUtils = (window.Landmark && window.Landmark.MapUtils) || null;

  class CoordinateGrabberWidget {
    constructor() {
      this.map = null;
      this.currentSelectedArea = null;
      this.isValidSize = true; // Track if current size is valid
      this.mapSnapshot = null; // Store captured map image
      this.container = document.getElementById(containerId);

      // GLB Viewer properties
      this.glbViewer = null;
      this.glbViewerFinal = null;
      this.currentPreviewJobId = null;
      this.previewJobPromise = null;
      this.activePreviewJobToken = null;
      this.finalPreviewRetryTimer = null;
      this.finalPreviewRetryCount = 0;
      this.isGeneratingFinalGLB = false;
      this.isGeneratingPreviewGLB = false; // Track preview GLB generation state
      
      // GLB Caching properties
      this.cachedGLBUrl = null;
      this.cachedGLBCoordinates = null;
      this.finalJobId = null;
      this.finalGlbUrl = null;
      this.finalGlbCoordinates = null;
      this.finalEngravingText = null;
      
      // Rotation state management
      this.mapRotation = 0; // 0, 90, 180, 270 degrees (North, East, South, West)
      this.rotationButton = null;
      this.rotationIndicator = null;
      this.isRotating = false; // Track if rotation is in progress

      // Selected area indicator management
      this.selectedAreaIndicator = null;
      this.selectedAreaLabel = null;
      this.backendUrl = backendUrl; // Expose validated backend URL
      this.freeMockupVariantId = freeMockupVariantId;
      this.finalAddToCartButton = null;
      this.freeMockupButton = null;
      this.mapUtilsMissingWarned = false;

      const urlParams = new URLSearchParams(window.location.search);
      const disableWebGLParam = urlParams.get('disableWebGL');
      this.forceDisableWebGL = config.forceDisableWebGL === true || disableWebGLParam === '1';
      const glbSupportAvailable = GLBViewer && typeof GLBViewer.isWebGLAvailable === 'function'
        ? GLBViewer.isWebGLAvailable()
        : false;
      this.webglSupported = !this.forceDisableWebGL && glbSupportAvailable;
      this.previewImageEl = null;
      this.previewImageFinalEl = null;
      this.currentPreviewPNGUrl = null;
      this.currentPreviewPngJobId = null;

      if (!this.container) {
        console.error(`Container with ID '${containerId}' not found`);
        return;
      }
      this.init();
    }

    async init() {

      // Render the widget HTML
      this.renderWidget();

      // Load Mapbox GL JS if not already loaded
      if (typeof mapboxgl === 'undefined') {
        await this.loadMapboxGL();
      }

      this.initializeMap();
      this.setupEventListeners();
      this.controlShopifyAddToCart(null); // Initially disable add to cart
    }

    renderWidget() {
      this.container.innerHTML = `
        <div class="coordinate-grabber">
          <div class="area-selector-header">
            <h3 class="area-selector-title">Choose Your Landmark</h3>
            <div class="step-indicators">
              <div class="step-indicator active" id="step-1">
                <span class="step-number">1</span>
                <span class="step-text">Select Map Area</span>
              </div>
              <div class="step-indicator" id="step-2">
                <span class="step-number">2</span>
                <span class="step-text">Enter Engraving</span>
              </div>
              <div class="step-indicator" id="step-3">
                <span class="step-number">3</span>
                <span class="step-text">Confirm</span>
              </div>
            </div>
          </div>

            <div class="area-selector-content">
              <div class="step-content" id="step-1-content">
                <p class="step-instruction">Drag the map to the area you wish to select. Use the rotate button to adjust the orientation behind the frame.</p>
                <div class="map-container">
                  <div id="area-selector-map" class="area-map"></div>
                <div class="map-style-selector">
                  <button class="style-btn active" data-style="mapbox://styles/mapbox/outdoors-v12">Terrain</button>
                  <button class="style-btn" data-style="mapbox://styles/mapbox/satellite-v9">Satellite</button>
                  <button id="rotate-map" class="style-btn rotate-btn" title="Rotate Map 90°">
                    ↻ Rotate Map <span id="rotation-indicator">N</span>
                  </button>
                </div>
                  <div class="rectangle-overlay">
                  <div class="distance-overlay horizontal-distance" id="horizontal-distance">
                    <span class="distance-text">-</span>
                  </div>
                  <div class="distance-overlay vertical-distance" id="vertical-distance">
                    <span class="distance-text">-</span>
                  </div>
                  <div class="engraving-text" id="engraving-text">ENGRAVING GOES HERE</div>
                </div>
              </div>

              <!-- Validation message display - above buttons -->
              <div class="validation-message" id="validation-message">
              </div>

              <div class="area-controls">
                <button id="confirm-area" class="confirm-area-button" disabled>
                  Select This Area
                </button>
                <div class="area-selected-controls">
                  <button id="clear-area" class="clear-area-button">
                    Change Area
                  </button>
                  <button id="continue-to-engraving" class="continue-area-button">
                    Continue with selected area
                  </button>
                </div>
              </div>

              <!-- Step 1: GLB Preview Container -->
              <div class="glb-preview-container" id="glb-preview-container">
                <h3 class="glb-preview-title">3D Topography Preview</h3>
                <div class="glb-viewer-container">
                  <div id="glb-progress-overlay" class="glb-progress-overlay">
                    <div class="glb-progress-text" id="glb-progress-text">Preparing 3D preview...</div>
                  </div>
                  <div id="glb-viewer-loading" class="glb-loading">
                    <div class="glb-spinner"></div>
                    <div class="glb-loading-text">Generating 3D preview...</div>
                  </div>
                  <canvas id="glb-viewer-canvas" class="glb-viewer-canvas"></canvas>
                  <img id="glb-preview-image" class="glb-preview-image" alt="Topography preview" />
                </div>
                <div class="glb-preview-status" id="glb-preview-status"></div>
                <div class="notification-text">
                  PLEASE NOTE: This is a quick low res preview of your selected map topology to give you an idea of what it will look like. After the order is placed, you will receive an email within 2 days with a high detail mockup of your Landmark. Production won't start until you have confirmed this and are happy with how it looks.
                </div>
              </div>

              <!-- Coordinates display - back at the bottom -->
              <div class="area-display" id="area-display">
              </div>
            </div>

            <div class="step-content hidden" id="step-2-content">
              <p class="step-instruction">Enter your engraving. This is the main front engraving and will be all caps (max 20 characters)</p>
              <div class="place-name-section">
                <div class="input-group">
                  <label for="place-name-input" class="input-label">Enter your front engraving in the box below:</label>
                  <input 
                    type="text" 
                    id="place-name-input" 
                    class="place-name-input" 
                    maxlength="20" 
                    placeholder="Examples: SNOWDON or 53.0846° N 4.0872° W, or OUR SPOT"
                  />
                  <div class="character-count">
                    <span id="char-count">0</span>/20
                  </div>
                </div>

                <div class="engraving-example-container">
                  <img 
                    src="https://cdn.shopify.com/s/files/1/0550/7861/7174/files/EngravingTextCloseUpSmall.png?v=1760448602" 
                    alt="Example of engraving text on landmark" 
                    class="engraving-example-image"
                  />
                </div>

                <div class="engraving-preview" id="engraving-preview">
                  <strong>Engraving Preview:</strong> <span class="preview-text">Enter engraving above</span>
                </div>
              </div>

              <div class="final-controls">
                <button id="back-to-map" class="back-button">
                  ← Back to Map
                </button>
                <button id="confirm-order" class="confirm-order-button" disabled>
                  Confirm Engraving
                </button>
              </div>
            </div>

            <div class="step-content hidden" id="step-3-content">
              <div class="confirmation-header">
                <h4 class="ready-message">✓ Ready to Add to Cart</h4>
              </div>
              
              <div class="confirmation-details">
                <div class="detail-section map-snapshot-section">
                  <h5 class="detail-title">Selected Map Area:</h5>
                  <div class="map-snapshot-container">
                    <img id="step3-map-snapshot" class="map-snapshot" alt="Selected map area" />
                  </div>
                </div>

                <div class="detail-section preview-section">
                  <h5 class="detail-title">3D Topography Preview</h5>
                  <div class="preview-container">
                    <div class="glb-viewer-container-final">
                      <div id="glb-progress-overlay-final" class="glb-progress-overlay">
                        <div class="glb-progress-text" id="glb-progress-text-final">Preparing 3D preview...</div>
                      </div>
                      <div id="glb-viewer-loading-final" class="glb-loading">
                        <div class="glb-spinner"></div>
                        <div class="glb-loading-text">Generating final 3D preview...</div>
                      </div>
                      <canvas id="glb-viewer-canvas-final" class="glb-viewer-canvas-final"></canvas>
                      <img id="glb-preview-image-final" class="glb-preview-image" alt="Topography preview" />
                    </div>
                    <div id="topomap-preview-status" class="preview-status"></div>
                  </div>
                  <div class="notification-text">
                    PLEASE NOTE: This is a quick low res preview of your selected map topology to give you an idea of what it will look like. After the order is placed, you will receive an email within 2 days with a high detail mockup of your Landmark. Production won't start until you have confirmed this and are happy with how it looks.
                  </div>
                </div>

                <div class="detail-section coordinates-section">
                  <h5 class="detail-title">Coordinates:</h5>
                  <div class="coordinates-display">
                    <div class="coordinate-line">
                      <span class="coord-label">North-East:</span>
                      <span id="step3-ne-coords" class="coord-value">--</span>
                    </div>
                    <div class="coordinate-line">
                      <span class="coord-label">South-West:</span>
                      <span id="step3-sw-coords" class="coord-value">--</span>
                    </div>
                  </div>
                </div>

                <div class="detail-section engraving-section">
                  <h5 class="detail-title">Engraving Text:</h5>
                  <div id="step3-engraving" class="engraving-value">--</div>
                </div>
              </div>

              <div class="step3-actions">
                <div class="cart-button-container">
                  <!-- Add to cart button will be inserted here -->
                </div>
                
                <div class="purchase-note">
                  <p>Add it to your cart to checkout now, or choose \"Get a Free Mockup\" to order a free no obligation detailed preview. If you add to cart and purchase first, you will still receive the detailed preview before production goes ahead and have the chance to change or cancel for a full refund. We have to manually make the mockup so it can take a day or two.</p>
                </div>
                
                <button class="change-selection-button" onclick="window.location.reload()">
                  Start Over
                </button>
              </div>
            </div>
          </div>
        </div>
      `;

      // Add CSS styles
      this.addStyles();

      // Get DOM references
      this.mapContainer = document.getElementById('area-selector-map');
      this.confirmButton = document.getElementById('confirm-area');
      this.clearButton = document.getElementById('clear-area');
      this.areaDisplay = document.getElementById('area-display');
      this.validationMessage = document.getElementById('validation-message');

      // V2 Step flow references
      this.step1Content = document.getElementById('step-1-content');
      this.step2Content = document.getElementById('step-2-content');
      this.step3Content = document.getElementById('step-3-content');
      this.step1Indicator = document.getElementById('step-1');
      this.step2Indicator = document.getElementById('step-2');
      this.step3Indicator = document.getElementById('step-3');
      this.placeNameInput = document.getElementById('place-name-input');
      this.charCount = document.getElementById('char-count');
      this.engravingPreview = document.getElementById('engraving-preview');
      this.backButton = document.getElementById('back-to-map');
      this.confirmOrderButton = document.getElementById('confirm-order');
      this.previewImageEl = document.getElementById('glb-preview-image');
      this.previewImageFinalEl = document.getElementById('glb-preview-image-final');

      // Initialize state
      this.currentStep = 1;
      this.placeName = '';

      // Setup place name event listeners after DOM is ready
      setTimeout(() => {
        this.setupPlaceNameListeners();
      }, 100);
    }

    addStyles() {
      const existingStyles = document.getElementById('coordinate-grabber-styles');
      if (existingStyles) return;

      const styleSheet = document.createElement('style');
      styleSheet.id = 'coordinate-grabber-styles';
      styleSheet.textContent = `
        .coordinate-grabber {
          margin: 20px 0;
          padding: 20px;
          border: 1px solid #3A3A3A;
          border-radius: 8px;
          background: #F8E9D6;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* Desktop side padding */
        @media (min-width: 769px) {
          .coordinate-grabber {
            margin: 20px 80px;
          }
        }

        .step-indicators {
          display: flex;
          justify-content: center;
          gap: 40px;
          margin: 16px 0;
          padding: 0;
        }

        .step-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: #3A3A3A;
          transition: color 0.3s ease;
        }

        /* Mobile-specific fixes for step indicators */
        @media (max-width: 768px) {
          .step-indicators {
            gap: 20px;
            justify-content: space-between;
            padding: 0 10px;
          }
          
          .step-indicator {
            flex-direction: column;
            text-align: center;
            gap: 4px;
            min-width: 60px;
          }
          
          .step-text {
            font-size: 12px;
            line-height: 1.2;
            white-space: nowrap;
          }
          
          .step-number {
            width: 24px !important;
            height: 24px !important;
            min-width: 24px !important;
            min-height: 24px !important;
            flex-shrink: 0 !important;
          }
        }

        .step-indicator.active {
          color: #DD7531;
          font-weight: 600;
        }

        .step-indicator.completed {
          color: #3A3A3A;
        }

        .step-number {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background-color: #3A3A3A;
          color: #F8E9D6;
          font-size: 12px;
          font-weight: 600;
          flex-shrink: 0;
        }

        .step-indicator.active .step-number {
          background-color: #DD7531;
          color: #F8E9D6;
        }

        .step-indicator.completed .step-number {
          background-color: #3A3A3A;
          color: #F8E9D6;
        }

        .step-content {
          display: block;
        }

        .step-content.hidden {
          display: none;
        }

        .step-instruction {
          font-size: 14px;
          color: #666;
          margin: 0 0 16px 0;
          text-align: center;
        }

        .place-name-section {
          background: #F8E9D6;
          border: 1px solid #3A3A3A;
          border-radius: 6px;
          padding: 20px;
          margin: 16px 0;
          text-align: center;
        }

        .input-group {
          margin-bottom: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .input-label {
          display: block;
          font-weight: 600;
          margin-bottom: 8px;
          color: #3A3A3A;
          text-align: center;
        }

        .place-name-input {
          width: 100%;
          max-width: 400px;
          padding: 12px;
          border: 1px solid #3A3A3A;
          border-radius: 4px;
          font-size: 16px;
          font-family: inherit;
          box-sizing: border-box;
          background: #F8E9D6;
          color: #3A3A3A;
        }

        .place-name-input:focus {
          outline: none;
          border-color: #3A3A3A;
          box-shadow: 0 0 0 2px rgba(58, 58, 58, 0.1);
        }

        .character-count {
          text-align: center;
          margin-top: 4px;
          font-size: 12px;
          color: #666;
        }

        .engraving-example-container {
          margin: 16px 0;
          text-align: center;
        }

        .engraving-example-image {
          max-width: 100%;
          width: auto;
          height: auto;
          max-height: 200px;
          border-radius: 6px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border: 1px solid #ddd;
        }

        .engraving-preview {
          background: #F8E9D6;
          border: none;
          border-radius: 4px;
          padding: 12px;
          font-size: 14px;
          text-align: center;
        }

        .preview-text {
          font-style: italic;
          color: #666;
          text-align: center;
        }

        .final-controls {
          display: flex;
          gap: 12px;
          justify-content: space-between;
          margin-top: 20px;
        }

        .back-button {
          padding: 10px 20px;
          border: 1px solid #3A3A3A;
          border-radius: 6px;
          background: #F8E9D6;
          color: #3A3A3A;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .back-button:hover {
          background: #ede0cc;
          border-color: #2a2a2a;
        }

        .confirm-order-button {
          padding: 14px 20px;
          border: none;
          border-radius: 6px;
          background-color: #3A3A3A;
          color: #F8E9D6;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .confirm-order-button:hover:not(:disabled) {
          background-color: #2a2a2a;
        }

        .confirm-order-button:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }

        .area-selector-header {
          margin-bottom: 16px;
        }

        .area-selector-title {
          font-family: 'Newake', 'Inter', sans-serif;
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 4px 0;
          color: #3A3A3A;
        }

        .area-selector-subtitle {
          font-size: 14px;
          color: #666;
          margin: 0;
        }

        .area-selector-content {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .map-container {
          position: relative;
          height: 350px;
          border-radius: 6px;
          overflow: hidden;
          border: 1px solid #ddd;
        }

        @media (max-width: 768px) {
          .map-container {
            height: 320px !important;
          }
        }

        .area-map {
          width: 100%;
          height: 100%;
        }

        .coordinate-grabber .rectangle-overlay {
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          width: 200px !important;
          height: 116px !important;
          transform: translate(-50%, -50%) !important;
          border: 3px solid #3A3A3A !important;
          background-color: transparent !important;
          pointer-events: none !important;
          z-index: 1000 !important;
          border-radius: 4px !important;
          display: block !important;
          visibility: visible !important;
          transition: width 0.3s ease, height 0.3s ease !important;
        }

        .area-controls {
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .confirm-area-button, .clear-area-button {
          padding: 20px 20px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          min-height: 60px;
          box-sizing: border-box;
        }

        .confirm-area-button {
          background-color: #4264fb;
          color: white;
        }

        .confirm-area-button:hover:not(:disabled) {
          background-color: #3b5cff;
        }

        .confirm-area-button:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }

        .clear-area-button {
          background-color: #f8f9fa;
          color: #333;
          border: 1px solid #ddd;
        }

        .clear-area-button:hover {
          background-color: #e9ecef;
        }

        .area-display {
          background-color: #f8f9fa;
          border-radius: 6px;
          padding: 16px;
          text-align: center;
          min-height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .area-display p {
          margin: 0;
          line-height: 1.5;
          color: #333;
          font-size: 14px;
        }

        .area-display.has-data {
          text-align: left;
          background-color: #e8f5e8;
          border: 1px solid #4caf50;
        }

        .area-display.has-data p {
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 13px;
          white-space: pre-line;
        }

        .coordinate-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(58, 58, 58, 0.9);
          color: #F8E9D6;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: not-allowed;
          z-index: 1000;
          pointer-events: auto;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
        }

        .order-complete {
          text-align: center;
        }

        .final-summary {
          text-align: center;
          margin: 20px 0;
        }

        .summary-section {
          margin-bottom: 16px;
          text-align: center;
        }

        .final-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-items: center;
          margin-top: 20px;
        }

        .purchase-note {
          text-align: center;
          margin: 16px 0;
          padding: 12px 16px;
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          max-width: 500px;
        }

        .purchase-note p {
          margin: 0;
          font-size: 14px;
          color: #666;
          line-height: 1.4;
        }

        .map-snapshot-section {
          margin-bottom: 20px;
        }

        .map-snapshot {
          max-width: 300px;
          width: 100%;
          height: auto;
          border: 2px solid #3A3A3A;
          border-radius: 6px;
          margin-top: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .glb-preview-container {
          margin: 24px 0;
          text-align: center;
        }

        .glb-preview-title {
          font-family: 'Newake', 'Inter', sans-serif;
          font-size: 18px;
          font-weight: 600;
          color: #3A3A3A;
          margin: 0 0 12px 0;
        }

        .glb-preview-image {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: none;
          background: #3A3A3A;
        }

        .glb-viewer-container {
          position: relative;
          width: 100%;
          height: 300px;
          background: #3A3A3A;
          overflow: hidden;
          margin: 12px 0;
        }

        .glb-reset-camera-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 20;
          background: rgba(248, 233, 214, 0.9);
          border: 1px solid #3A3A3A;
          border-radius: 4px;
          padding: 8px 12px;
          font-size: 12px;
          font-weight: 600;
          color: #3A3A3A;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Inter', sans-serif;
        }

        .glb-reset-camera-btn:hover {
          background: #F8E9D6;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .glb-reset-camera-btn:active {
          transform: translateY(0);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        }

        .glb-viewer-canvas {
          width: 100%;
          height: 100%;
          display: block;
        }

        .glb-loading {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #3A3A3A;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #F8E9D6;
          z-index: 10;
        }

        .glb-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(248, 233, 214, 0.2);
          border-top-color: #F8E9D6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 15px;
        }

        .glb-loading-text {
          font-size: 14px;
          font-weight: 500;
        }

        .glb-preview-status {
          font-size: 13px;
          color: #666;
          font-style: italic;
          margin-top: 8px;
        }

        .glb-progress-overlay {
          position: absolute;
          inset: 0;
          background: rgba(58, 58, 58, 0.92);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          color: #F8E9D6;
          z-index: 15;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s ease;
        }

        .glb-progress-overlay.active {
          opacity: 1;
          pointer-events: auto;
        }

        .glb-progress-bar {
          position: relative;
          width: 70%;
          max-width: 320px;
          height: 8px;
          border-radius: 4px;
          background: rgba(248, 233, 214, 0.2);
          border: 1px solid rgba(248, 233, 214, 0.35);
          overflow: hidden;
        }

        .glb-progress-fill {
          position: absolute;
          inset: 0;
          width: 100%;
          background: linear-gradient(90deg, #DD7531 0%, #F6B36E 100%);
          transform-origin: left center;
          transform: scaleX(0);
          transition: transform 0.25s ease;
          box-shadow: 0 0 6px rgba(246, 179, 110, 0.35);
        }

        .glb-progress-text {
          font-size: 14px;
          font-weight: 600;
          text-align: center;
          letter-spacing: 0.02em;
        }

        .glb-viewer-container-final {
          position: relative;
          width: 100%;
          height: 400px;
          background: #3A3A3A;
          overflow: hidden;
          margin: 12px 0;
        }

        .glb-viewer-canvas-final {
          width: 100%;
          height: 100%;
          display: block;
        }

        .custom-compass {
          position: absolute;
          top: 90px;
          right: 10px;
          width: 40px;
          height: 40px;
          background: #F8E9D6;
          border: 2px solid #3A3A3A;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
          z-index: 1000;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          transition: transform 0.3s ease;
        }

        .compass-needle {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.3s ease;
        }

        @media (max-width: 768px) {
          .coordinate-grabber {
            padding: 16px;
          }

          .map-container {
            height: 320px !important;
          }

          .rectangle-overlay {
            width: 160px;
            height: 80px;
          }

          .area-controls {
            flex-direction: column;
          }

          .confirm-area-button, .clear-area-button {
            width: 100%;
          }

          .custom-compass {
            width: 35px;
            height: 35px;
            top: 80px;
            right: 8px;
          }

          .engraving-example-image {
            max-height: 150px;
          }
        }
      `;

      document.head.appendChild(styleSheet);
    }

    loadMapboxGL() {
      return new Promise((resolve, reject) => {
        if (typeof mapboxgl !== 'undefined') {
          resolve();
          return;
        }

        const existingScript = document.querySelector('script[data-mapbox-gl]');
        if (existingScript) {
          existingScript.addEventListener('load', () => resolve(), { once: true });
          existingScript.addEventListener('error', () => reject(new Error('Failed to load Mapbox GL JS')), { once: true });
          return;
        }

        const link = document.createElement('link');
        link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css';
        link.rel = 'stylesheet';
        link.crossOrigin = 'anonymous';
        link.integrity = 'sha384-SDYx9Nwa5fE1fRuBplOPejrcbPOK/ql0Uym6hsGsTvnlC784P5LZhBJIbo8O/O+0';
        document.head.appendChild(link);

        const script = document.createElement('script');
        script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js';
        script.crossOrigin = 'anonymous';
        script.integrity = 'sha384-GCe89tb5amHPhp10tMEUmIOUpgyTbhqwThspGxJoQMvr5I6Zfq7lYU6ydn7dVKA6';
        script.setAttribute('data-mapbox-gl', 'true');
        script.onload = resolve;
        script.onerror = () => reject(new Error('Failed to load Mapbox GL JS'));
        document.head.appendChild(script);
      });
    }

    initializeMap() {
      if (!mapboxToken) {
        console.error('Mapbox token not configured');
        const errorParagraph = document.createElement('p');
        errorParagraph.className = 'map-error-message';
        errorParagraph.textContent = 'Map configuration error. Please check settings.';
        this.areaDisplay.appendChild(errorParagraph);
        return;
      }

      mapboxgl.accessToken = mapboxToken;

      // Parse URL parameters for initial position
      const urlParams = new URLSearchParams(window.location.search);
      const zoom = parseFloat(urlParams.get('zoom')) || 10;
      const lat = parseFloat(urlParams.get('lat')) || 53.06906901194867;
      const lng = parseFloat(urlParams.get('lng')) || -4.076105077951168;

      this.map = new mapboxgl.Map({
        container: this.mapContainer,
        style: 'mapbox://styles/mapbox/outdoors-v12',
        center: [lng, lat],
        zoom: zoom,
        antialias: true,
        bearing: 0,
        pitch: 0,
        dragRotate: false, // Disable drag rotation - only allow programmatic rotation via button
        touchZoomRotate: true, // Enable touch zoom but we'll disable rotation separately
        pitchWithRotate: false,
        touchPitch: false,
        preserveDrawingBuffer: true  // This is crucial for canvas capture!
      });

      // Disable rotation while keeping zoom - override the touch rotation handler
      this.map.touchZoomRotate.disableRotation();
      
      // Add zoom control only (no compass/rotation)
      this.map.addControl(new mapboxgl.NavigationControl({
        showCompass: false,
        showZoom: true,
        visualizePitch: false
      }), 'top-right');

      // Add custom non-interactive compass
      this.addCustomCompass();

      // Enable confirm button once map is loaded
      this.map.on('load', () => {
        // Force border color on map load
        const rectangleOverlay = document.querySelector('.rectangle-overlay');
        if (rectangleOverlay) {
          rectangleOverlay.style.border = '3px solid #3A3A3A';
        }
        this.updateDistanceOverlays(); // V3: Initialize distance calculations
        this.updateButtonState(); // Set initial button state based on validation
        this.showTemplateOverlay(); // Show template overlay on map load

        // Set up interval to continuously enforce border color
        setInterval(() => {
          this.enforceBorderColor();
        }, 1000);
      });

      this.map.on('pitch', () => {
        this.map.setPitch(0);
      });

      this.map.on('move', () => {
        this.updateDistanceOverlays(); // V3: Update distances on map move
        this.enforceBorderColor(); // Ensure border stays dark
        this.updateSelectedAreaIndicator(); // Update selected area indicator position
      });

      this.map.on('zoom', () => {
        this.updateDistanceOverlays(); // V3: Update distances on zoom
        this.enforceBorderColor(); // Ensure border stays dark
        this.updateSelectedAreaIndicator(); // Update selected area indicator position
      });

      // Update compass on rotation
      this.map.on('rotate', () => {
        this.updateCompass();
        this.updateSelectedAreaIndicator(); // Update selected area indicator position
      });
    }

    addCustomCompass() {
      // Create compass container
      const compassContainer = document.createElement('div');
      compassContainer.className = 'custom-compass';

      // Create compass needle/indicator (simplified to just north arrow)
      const compassNeedle = document.createElement('div');
      compassNeedle.className = 'compass-needle';

      // North arrow (orange triangle pointing up)
      const northArrow = document.createElement('div');
      northArrow.style.cssText = `
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-bottom: 16px solid #DD7531;
      `;

      // North label "N"
      const northLabel = document.createElement('div');
      northLabel.textContent = 'N';
      northLabel.style.cssText = `
        position: absolute;
        top: 2px;
        left: 50%;
        transform: translateX(-50%);
        font-size: 10px;
        font-weight: 700;
        color: #3A3A3A;
        font-family: 'Inter', sans-serif;
        line-height: 1;
      `;

      // Assemble simplified compass (just north arrow)
      compassNeedle.appendChild(northArrow);
      compassNeedle.appendChild(northLabel);
      compassContainer.appendChild(compassNeedle);

      // Add to map container positioned below zoom controls
      const mapContainer = this.mapContainer;
      if (mapContainer && mapContainer.parentElement) {
        mapContainer.parentElement.style.position = 'relative';
        mapContainer.parentElement.appendChild(compassContainer);
        
        // Store reference for updates
        this.compassContainer = compassContainer;
        this.compassNeedle = compassNeedle;
        
        // Initial compass update
        this.updateCompass();
      }
    }

    updateCompass() {
      if (!this.compassNeedle || !this.map) return;
      
      // Get current map bearing (rotation)
      const bearing = this.map.getBearing();
      
      // Rotate the needle opposite to the map bearing so North always points up
      this.compassNeedle.style.transform = `rotate(${-bearing}deg)`;
    }

    setupEventListeners() {

      // Step 1: Map selection
      if (this.confirmButton) {
        this.confirmButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.confirmCoordinates();
        });
      }

      if (this.clearButton) {
        this.clearButton.addEventListener('click', (e) => {
          e.preventDefault();
          // Prevent changing area while GLB is generating
          if (this.isGeneratingPreviewGLB) {
            console.log('[CoordinateWidget] Ignoring change area click - GLB generation in progress');
            return;
          }
          this.changeArea();
        });
      }

      // Map style selector
      const styleButtons = this.container.querySelectorAll('.style-btn');
      styleButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const style = button.getAttribute('data-style');
          if (style) {
            this.changeMapStyle(style, button);
          } else if (button.id === 'rotate-map') {
            this.rotateMap90Degrees();
          }
        });
      });

      // Step 2: Place name input
      if (this.placeNameInput) {
        this.placeNameInput.addEventListener('input', (e) => {
          this.updatePlaceName(e.target.value);
        });
      }

      if (this.backButton) {
        this.backButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.goBackToStep1();
        });
      }

      if (this.confirmOrderButton) {
        this.confirmOrderButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.finalConfirmOrder();
        });
      }

      // Continue to engraving button
      const continueToEngravingButton = document.getElementById('continue-to-engraving');
      if (continueToEngravingButton) {
        continueToEngravingButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.goToStep2();
        });
      }
    }

    // V2 Step Navigation Methods
    goToStep1() {
      this.currentStep = 1;
      this.step1Content.classList.remove('hidden');
      this.step2Content.classList.add('hidden');
      this.step1Indicator.classList.add('active');
      this.step1Indicator.classList.remove('completed');
      this.step2Indicator.classList.remove('active');
      if (this.step3Indicator) this.step3Indicator.classList.remove('active');

      // Show template overlay on step 1 (map selection)
      this.showTemplateOverlay();

      this.controlShopifyAddToCart(null);
    }

    goToStep2() {
      this.currentStep = 2;
      this.step1Content.classList.add('hidden');
      this.step2Content.classList.remove('hidden');
      this.step1Indicator.classList.remove('active');
      this.step1Indicator.classList.add('completed');
      this.step2Indicator.classList.add('active');
      if (this.step3Indicator) this.step3Indicator.classList.remove('active');

      // Focus on place name input
      setTimeout(() => {
        if (this.placeNameInput) {
          this.placeNameInput.focus();
        }
      }, 100);
    }

    goToStep3() {
      this.currentStep = 3;
      if (this.step1Content) this.step1Content.classList.add('hidden');
      if (this.step2Content) this.step2Content.classList.add('hidden');
      if (this.step3Content) this.step3Content.classList.remove('hidden');
      if (this.step1Indicator) this.step1Indicator.classList.remove('active');
      if (this.step2Indicator) {
        this.step2Indicator.classList.remove('active');
        this.step2Indicator.classList.add('completed');
      }
      if (this.step3Indicator) this.step3Indicator.classList.add('active');
      this.resetFinalPreviewPolling();

      // Populate Step 3 with confirmation details
      this.populateStep3Details();
    }

    showTemplateOverlay() {
      const engravingText = document.getElementById('engraving-text');
      if (engravingText) {
        engravingText.style.display = 'block';
      }
    }

    hideTemplateOverlay() {
      const engravingText = document.getElementById('engraving-text');
      if (engravingText) {
        engravingText.style.display = 'none';
      }
    }

    enforceBorderColor() {
      const rectangleOverlay = document.querySelector('.rectangle-overlay');
      if (rectangleOverlay) {
        rectangleOverlay.style.border = '3px solid #3A3A3A';
      }
    }

    goBackToStep1() {
      this.goToStep1();
      this.resetFinalPreviewPolling();
      
      // Clear job state to prevent pipeline conflicts
      if (this.__landmarkJobId) {
        this.__landmarkJobId = null;
        this.__landmarkJobStarted = false;
        this.__landmarkJobStarting = false;
      }
      
      // Keep coordinates but show area selected controls
      if (this.currentSelectedArea) {
        this.confirmButton.style.display = 'none';
        const areaSelectedControls = document.querySelector('.area-selected-controls');
        if (areaSelectedControls) {
          areaSelectedControls.style.display = 'flex';
        }
        // Keep the selected area indicator visible when going back to step 1
        this.showSelectedAreaIndicator();
      }

      // Ensure template overlay is shown when going back to step 1
      this.showTemplateOverlay();
    }

    updatePlaceName(value) {
      this.placeName = value.trim();

      // Update character count
      if (this.charCount) {
        this.charCount.textContent = value.length;
      }

      // Update preview
      const previewSpan = this.engravingPreview.querySelector('.preview-text');
        if (previewSpan) {
          if (this.placeName.length > 0) {
            previewSpan.textContent = this.placeName.toUpperCase();
            previewSpan.style.fontStyle = 'normal';
            previewSpan.style.color = '#333';
            previewSpan.style.fontFamily = "'Newake', 'Inter', sans-serif";
            previewSpan.style.fontWeight = '600';
            previewSpan.style.fontSize = '18px';
          } else {
            previewSpan.textContent = 'Enter place name above';
            previewSpan.style.fontStyle = 'italic';
            previewSpan.style.color = '#666';
            previewSpan.style.fontFamily = "'Newake', 'Inter', sans-serif";
            previewSpan.style.fontWeight = 'normal';
            previewSpan.style.fontSize = '14px';
          }
        }
        const overlayText = this.placeName.length > 0 ? this.placeName.toUpperCase() : '';
        if (this.glbViewer && typeof this.glbViewer.setEngravingText === 'function') {
          this.glbViewer.setEngravingText(overlayText);
        }
        if (this.glbViewerFinal && typeof this.glbViewerFinal.setEngravingText === 'function') {
          this.glbViewerFinal.setEngravingText(overlayText);
        }

        // Enable/disable Add to Cart button
        if (this.confirmOrderButton) {
        if (this.placeName.length > 0 && this.currentSelectedArea) {
          this.confirmOrderButton.disabled = false;
        } else {
          this.confirmOrderButton.disabled = true;
        }
      }
    }

    finalConfirmOrder() {
      if (!this.currentSelectedArea || !this.placeName) {
        console.error('Missing coordinates or place name');
        return;
      }


      // Navigate to Step 3 first
      this.goToStep3();

      // Generate final GLB after Step 3 is shown
      setTimeout(() => {
        this.generateFinalGLB();
      }, 100);
    }

    updateDistanceOverlays() {
      if (!this.map) return;
      if (!MapUtils) {
        if (!this.mapUtilsMissingWarned) {
          console.warn('[CoordinateGrabberWidget] MapUtils module not loaded; distance overlays disabled.');
          this.mapUtilsMissingWarned = true;
        }
        return;
      }

      const mapContainer = this.map.getContainer();
      const rect = mapContainer.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      // Rectangle dimensions based on screen size
      let rectWidth, rectHeight;
      const isMobile = window.innerWidth <= 768;

      rectWidth = isMobile ? 160 : 200;
      rectHeight = isMobile ? 93 : 116; // Maintain 1.7272 ratio

      // Calculate rectangle bounds in screen coordinates
      const left = centerX - rectWidth / 2;
      const right = centerX + rectWidth / 2;
      const top = centerY - rectHeight / 2;
      const bottom = centerY + rectHeight / 2;

      // Convert screen coordinates to geographic coordinates
      const topLeft = this.map.unproject([left, top]);
      const topRight = this.map.unproject([right, top]);
      const bottomLeft = this.map.unproject([left, bottom]);

      // Calculate horizontal distance (top edge)
      const horizontalDistance = MapUtils.calculateDistance(
        topLeft.lat, topLeft.lng,
        topRight.lat, topRight.lng
      );

      // Calculate vertical distance (left edge)  
      const verticalDistance = MapUtils.calculateDistance(
        topLeft.lat, topLeft.lng,
        bottomLeft.lat, bottomLeft.lng
      );

      // Validate size against limits - check the longer edge regardless of orientation
      const longerDistance = Math.max(horizontalDistance, verticalDistance);
      const longerDistanceMeters = longerDistance * 1000; // Convert to meters
      const wasValid = this.isValidSize;
      this.isValidSize = longerDistanceMeters >= VALIDATION_CONFIG.horizontalMinSize && 
                        longerDistanceMeters <= VALIDATION_CONFIG.horizontalMaxSize;

      // Update rectangle color if validation status changed
      if (wasValid !== this.isValidSize) {
        this.updateRectangleColor();
        this.updateButtonState();
      }

      // Update display
      const horizontalElement = document.getElementById('horizontal-distance');
      const verticalElement = document.getElementById('vertical-distance');

      if (horizontalElement) {
        const distanceText = horizontalElement.querySelector('.distance-text');
        if (distanceText) {
          distanceText.textContent = MapUtils.formatDistance(horizontalDistance);
        }
      }

      if (verticalElement) {
        const distanceText = verticalElement.querySelector('.distance-text');
        if (distanceText) {
          distanceText.textContent = MapUtils.formatDistance(verticalDistance);
        }
      }

      // Update error message if invalid
      this.updateValidationMessage();
    }

    updateRectangleColor() {
      const rectangleOverlay = document.querySelector('.rectangle-overlay');
      if (rectangleOverlay) {
        const color = this.isValidSize ? VALIDATION_CONFIG.validColor : VALIDATION_CONFIG.invalidColor;
        rectangleOverlay.style.border = `2px solid ${color}`;
      }
    }

    updateButtonState() {
      if (this.confirmButton) {
        this.confirmButton.disabled = !this.isValidSize;
        this.confirmButton.style.opacity = this.isValidSize ? '1' : '0.5';
        this.confirmButton.style.cursor = this.isValidSize ? 'pointer' : 'not-allowed';
      }
    }

    updateValidationMessage() {
      if (!this.validationMessage) return;
      if (!MapUtils) {
        if (!this.mapUtilsMissingWarned) {
          console.warn('[CoordinateGrabberWidget] MapUtils module not loaded; validation messaging disabled.');
          this.mapUtilsMissingWarned = true;
        }
        this.validationMessage.textContent = '';
        this.validationMessage.style.display = 'none';
        return;
      }

      if (!this.isValidSize) {
        const mapContainer = this.map.getContainer();
        const rect = mapContainer.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const isMobile = window.innerWidth <= 768;
        const rectWidth = isMobile ? 160 : 200;
        const rectHeight = isMobile ? 93 : 116;

        const left = centerX - rectWidth / 2;
        const top = centerY - rectHeight / 2;
        const right = centerX + rectWidth / 2;
        const bottom = centerY + rectHeight / 2;

        const topLeft = this.map.unproject([left, top]);
        const topRight = this.map.unproject([right, top]);
        const bottomLeft = this.map.unproject([left, bottom]);

        const horizontalDistance = MapUtils.calculateDistance(
          topLeft.lat, topLeft.lng,
          topRight.lat, topRight.lng
        );
        const verticalDistance = MapUtils.calculateDistance(
          topLeft.lat, topLeft.lng,
          bottomLeft.lat, bottomLeft.lng
        );

        const longerDistanceMeters = Math.max(horizontalDistance, verticalDistance) * 1000;

        let message;
        if (longerDistanceMeters < VALIDATION_CONFIG.horizontalMinSize) {
          message = VALIDATION_CONFIG.tooSmallMessage;
        } else {
          message = VALIDATION_CONFIG.tooLargeMessage;
        }

        const errorParagraph = document.createElement('p');
        errorParagraph.style.color = '#dc3545';
        errorParagraph.textContent = message;
        this.validationMessage.textContent = '';
        this.validationMessage.appendChild(errorParagraph);
        this.validationMessage.style.display = 'block';
      } else {
        this.validationMessage.textContent = '';
        this.validationMessage.style.display = 'none';
      }
    }


    

    populateStep3Details() {
      if (!this.currentSelectedArea || !this.placeName) return;

      const formatCoordinate = (coord, isLat = false) => {
        const direction = isLat ? (coord >= 0 ? 'N' : 'S') : (coord >= 0 ? 'E' : 'W');
        return `${Math.abs(coord).toFixed(6)}° ${direction}`;
      };

      const { topRight, bottomLeft } = this.currentSelectedArea;

      // Display the already captured map snapshot
      const step3Snapshot = document.getElementById('step3-map-snapshot');
      if (step3Snapshot) {
        if (this.mapSnapshot) {
          step3Snapshot.src = this.mapSnapshot;
          step3Snapshot.style.display = 'block';
          step3Snapshot.style.width = '600px';
          step3Snapshot.style.height = 'auto';
          step3Snapshot.style.border = '2px solid #3A3A3A';
          step3Snapshot.style.borderRadius = '6px';
        } else {
          console.warn('⚠️ No map snapshot available - was area selected?');
          step3Snapshot.style.display = 'none';
        }
      }

      // Update coordinates
      const neCoords = document.getElementById('step3-ne-coords');
      const swCoords = document.getElementById('step3-sw-coords');
      if (neCoords) {
        neCoords.textContent = `${formatCoordinate(topRight.lat, true)}, ${formatCoordinate(topRight.lng)}`;
      }
      if (swCoords) {
        swCoords.textContent = `${formatCoordinate(bottomLeft.lat, true)}, ${formatCoordinate(bottomLeft.lng)}`;
      }

      // Update engraving text
      const engravingEl = document.getElementById('step3-engraving');
      if (engravingEl) {
        engravingEl.textContent = this.placeName.toUpperCase();
      }

      // Add the cloned add to cart button
      this.setupStep3AddToCartButton();
    }

    setupStep3AddToCartButton() {
      const cartButtonContainer = document.querySelector('.cart-button-container');
      if (!cartButtonContainer || !this.originalAddToCartButton) {
        return;
      }

      // Clear existing content
      cartButtonContainer.textContent = '';

      const buttonGroup = document.createElement('div');
      buttonGroup.className = 'cart-button-group';

      // Clone and setup the add to cart button
      const clonedButton = this.originalAddToCartButton.cloneNode(true);
      clonedButton.style.display = 'block';
      clonedButton.style.pointerEvents = 'auto';
      clonedButton.style.opacity = '1';
      clonedButton.id = 'final-add-to-cart';
      clonedButton.type = 'button';
      clonedButton.className = `${this.originalAddToCartButton.className} final-cart-button`.trim();
      clonedButton.textContent = 'Add to Cart';
      clonedButton.dataset.originalText = clonedButton.textContent;
      buttonGroup.appendChild(clonedButton);
      this.finalAddToCartButton = clonedButton;

      clonedButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.addToCartWithAreaData(this.originalForm, {
          triggerButton: clonedButton,
          loadingText: 'Adding...',
          successRedirect: '/cart'
        });
      }, true);

      if (this.freeMockupVariantId) {
        const mockupButton = document.createElement('button');
        mockupButton.type = 'button';
        mockupButton.id = 'free-mockup-request';
        mockupButton.className = 'final-cart-button mockup-request-button';
        mockupButton.textContent = 'Get a Free Mockup';
        mockupButton.dataset.originalText = mockupButton.textContent;

        mockupButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.addToCartWithAreaData(this.originalForm, {
            variantId: this.freeMockupVariantId,
            triggerButton: mockupButton,
            additionalProperties: {
              'Request Type': 'Free Mockup'
            },
            loadingText: 'Requesting...',
            successRedirect: '/checkout'
          });
        }, true);

        buttonGroup.appendChild(mockupButton);
        this.freeMockupButton = mockupButton;
      } else {
        this.freeMockupButton = null;
      }

      cartButtonContainer.appendChild(buttonGroup);

      // Ensure buttons start enabled
      this.toggleFinalActionButtonsDisabled(false);
      this.setButtonLoading(this.finalAddToCartButton, false);
      if (this.freeMockupButton) {
        this.setButtonLoading(this.freeMockupButton, false);
      }
    }

    setButtonLoading(button, isLoading, loadingText = 'Adding...') {
      if (!button) return;

      if (isLoading) {
        if (!button.dataset.originalText) {
          button.dataset.originalText = button.textContent;
        }
        button.disabled = true;
        button.classList.add('is-loading');
        if (loadingText) {
          button.textContent = loadingText;
        }
      } else {
        button.disabled = false;
        button.classList.remove('is-loading');
        if (button.dataset.originalText) {
          button.textContent = button.dataset.originalText;
        }
      }
    }

    toggleFinalActionButtonsDisabled(disabled, excludeButton = null) {
      const buttons = [this.finalAddToCartButton, this.freeMockupButton].filter(Boolean);
      buttons.forEach((btn) => {
        if (btn !== excludeButton) {
          btn.disabled = disabled;
        }
      });
    }

    setupFinalAddToCartButton() {
      const finalButton = document.getElementById('final-add-to-cart');
      if (finalButton && this.originalForm) {
        const buttonClickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.addToCartWithAreaData(this.originalForm);
        };

        finalButton.addEventListener('click', buttonClickHandler, true);
      }
    }

    setupPlaceNameListeners() {
      // Additional setup for place name features if needed
      if (this.placeNameInput) {
        // Prevent form submission on enter
        this.placeNameInput.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (this.placeName.length > 0 && this.currentSelectedArea) {
              this.finalConfirmOrder();
            }
          }
        });
      }
    }

    confirmCoordinates() {

      if (!this.map) {
        console.error('Map not initialized');
        return;
      }

      try {
        // Get the rectangle overlay element from the map container parent
        const mapContainerParent = this.mapContainer.parentElement;
        const rectangle = mapContainerParent.querySelector('.rectangle-overlay');
        if (!rectangle) {
          console.error('Rectangle overlay not found in map container');
          return;
        }


        // Get rectangle dimensions and position
        const rectBounds = rectangle.getBoundingClientRect();
        const mapBounds = this.mapContainer.getBoundingClientRect();


        // Calculate rectangle position relative to map container
        const rectLeft = rectBounds.left - mapBounds.left;
        const rectTop = rectBounds.top - mapBounds.top;
        const rectRight = rectLeft + rectBounds.width;
        const rectBottom = rectTop + rectBounds.height;

        // Convert pixel coordinates to lat/lng
        const topRight = this.map.unproject([rectRight, rectTop]);
        const bottomLeft = this.map.unproject([rectLeft, rectBottom]);

        if (!MapUtils) {
          if (!this.mapUtilsMissingWarned) {
            console.warn('[CoordinateGrabberWidget] MapUtils module not loaded; unable to compute transformed coordinates.');
            this.mapUtilsMissingWarned = true;
          }
          return;
        }

        // Transform coordinates based on map rotation to get correct geographic bounding box
        const transformedCoords = MapUtils.transformCoordinatesForRotation(
          topRight.lat, topRight.lng, 
          bottomLeft.lat, bottomLeft.lng, 
          this.mapRotation
        );

        // Store the selected area with transformed coordinates
        this.currentSelectedArea = {
          topRight: {
            lat: transformedCoords.topRight.lat,
            lng: transformedCoords.topRight.lng
          },
          bottomLeft: {
            lat: transformedCoords.bottomLeft.lat,
            lng: transformedCoords.bottomLeft.lng
          },
          rotation: this.mapRotation, // Store current rotation state
          timestamp: new Date().toISOString()
        };
        // Capture map snapshot immediately after confirming coordinates
        this.captureMapSnapshot();

        // Update UI
        this.displaySelectedArea();
        this.confirmButton.style.display = 'none';

        // Show area selected controls
        const areaSelectedControls = document.querySelector('.area-selected-controls');
        if (areaSelectedControls) {
          areaSelectedControls.style.display = 'flex';
        }

        // Show continue button immediately after area selection
        this.showContinueToEngravingButton();

        // Show the selected area indicator immediately
        this.showSelectedAreaIndicator();

        // Generate GLB preview for Step 1
        this.generateGLBPreview();


      } catch (error) {
        console.error('Error confirming coordinates:', error);
      }
    }

    changeMapStyle(styleUrl, clickedButton) {
      if (!this.map) {
        console.error('Map not initialized');
        return;
      }

      try {
        // Update map style
        this.map.setStyle(styleUrl);

        // Update button states
        const styleButtons = this.container.querySelectorAll('.style-btn');
        styleButtons.forEach(btn => {
          btn.classList.remove('active');
        });
        clickedButton.classList.add('active');

        // Wait for style to load before updating overlays and recapturing snapshot
        this.map.once('styledata', () => {

          // Update distance overlays after style change
          this.updateDistanceOverlays();

          // If area is already selected, recapture snapshot with new style
          if (this.currentSelectedArea) {
            setTimeout(() => {
              this.captureMapSnapshot();
            }, 1000); // Wait for style to fully load
          }
        });

      } catch (error) {
        console.error('Error changing map style:', error);
      }
    }

    rotateMap90Degrees() {
      if (!this.map) {
        console.error('Map not initialized');
        return;
      }

      // Prevent rotation if already rotating
      if (this.isRotating) {
        return;
      }

      try {
        // Set rotation flag and disable user interactions
        this.isRotating = true;
        this.disableMapInteractions();
        
        // Calculate the center point of the selection rectangle
        const rectangleCenter = this.getSelectionRectangleCenter();
        
        // Increment rotation by 90 degrees (clockwise)
        this.mapRotation = (this.mapRotation + 90) % 360;
        
        // Update rotation indicator immediately for responsive feedback
        const directions = ['N', 'E', 'S', 'W'];
        const indicator = document.getElementById('rotation-indicator');
        if (indicator) {
          indicator.textContent = directions[this.mapRotation / 90];
        }
        
        
        // Animate map rotation with smooth bezier curve easing
        const easingFn = MapUtils ? MapUtils.createBezierEasing(0.25, 0.46, 0.45, 0.94) : (t) => t;
        if (!MapUtils && !this.mapUtilsMissingWarned) {
          console.warn('[CoordinateGrabberWidget] MapUtils module not loaded; using linear easing for rotation.');
          this.mapUtilsMissingWarned = true;
        }

        this.map.easeTo({
          bearing: this.mapRotation,
          center: rectangleCenter,
          duration: 800,
          easing: easingFn // Satisfying ease-out curve
        });
        
        // Set up completion handler
        this.map.once('moveend', () => {
          
          // Re-enable user interactions
          this.enableMapInteractions();
          this.isRotating = false;
          
          // Update distance overlays after rotation completes
          this.updateDistanceOverlays();
          
          // Update compass after rotation completes
          this.updateCompass();
          
          // If we have a selected area, keep it visible but clear cache to force re-confirmation
          if (this.currentSelectedArea) {
            this.mapSnapshot = null; // Clear snapshot since rotation changes view
            // Clear GLB cache since rotation changes coordinates
            this.currentPreviewJobId = null;
            this.previewJobPromise = null;
            this.activePreviewJobToken = null;
            this.cachedGLBUrl = null;
            this.cachedGLBCoordinates = null;
            this.finalJobId = null;
            this.finalGlbUrl = null;
            this.finalGlbCoordinates = null;
            this.finalEngravingText = null;
            this.resetFinalPreviewPolling();
            this.isGeneratingFinalGLB = false;
            // Keep the selected area indicator visible - just update its position
            this.updateSelectedAreaIndicator();
            // Don't clear currentSelectedArea - keep it so user can see their selection
            // They can still use "Change Area" if they want to select the new rotated coordinates
          }
        });
        
        // Fallback: ensure interactions are re-enabled even if moveend doesn't fire
        setTimeout(() => {
          if (this.isRotating) {
            console.warn('Rotation timeout - force re-enabling interactions');
            this.enableMapInteractions();
            this.isRotating = false;
          }
        }, 1200); // Slightly longer than animation duration
        
      } catch (error) {
        console.error('Error rotating map:', error);
        // Ensure interactions are re-enabled on error
        this.enableMapInteractions();
        this.isRotating = false;
      }
    }

    getSelectionRectangleCenter() {
      // Get the rectangle overlay element
      const mapContainerParent = this.mapContainer.parentElement;
      const rectangle = mapContainerParent.querySelector('.rectangle-overlay');
      
      if (!rectangle) {
        // Fallback to current map center if no rectangle
        return this.map.getCenter();
      }

      // Get rectangle dimensions and position
      const rectBounds = rectangle.getBoundingClientRect();
      const mapBounds = this.mapContainer.getBoundingClientRect();

      // Calculate rectangle center in screen coordinates
      const rectCenterX = (rectBounds.left + rectBounds.right) / 2 - mapBounds.left;
      const rectCenterY = (rectBounds.top + rectBounds.bottom) / 2 - mapBounds.top;

      // Convert screen coordinates to geographic coordinates
      const centerPoint = this.map.unproject([rectCenterX, rectCenterY]);
      
      return centerPoint;
    }

    disableMapInteractions() {
      if (!this.map) return;
      
      // Disable all user interactions during rotation
      this.map.scrollZoom.disable();
      this.map.boxZoom.disable();
      this.map.dragPan.disable();
      this.map.keyboard.disable();
      this.map.doubleClickZoom.disable();
      this.map.touchZoomRotate.disable();
      
      // Disable area selection buttons during rotation
      this.disableAreaButtons();
      
      // Disable zoom control buttons during rotation
      this.disableZoomButtons();
      
    }

    enableMapInteractions() {
      if (!this.map) return;
      
      // Re-enable user interactions after rotation
      this.map.scrollZoom.enable();
      this.map.boxZoom.enable();
      this.map.dragPan.enable();
      this.map.keyboard.enable();
      this.map.doubleClickZoom.enable();
      this.map.touchZoomRotate.enable();
      
      // Re-disable rotation while keeping zoom enabled
      this.map.touchZoomRotate.disableRotation();
      
      // Re-enable area selection buttons after rotation
      this.enableAreaButtons();
      
      // Re-enable zoom control buttons after rotation
      this.enableZoomButtons();
      
    }

    disableAreaButtons() {
      if (this.confirmButton) {
        this.confirmButton.disabled = true;
        this.confirmButton.style.opacity = '0.5';
        this.confirmButton.style.cursor = 'not-allowed';
        this.confirmButton.style.pointerEvents = 'none';
      }

      if (this.clearButton) {
        this.clearButton.disabled = true;
        this.clearButton.style.opacity = '0.5';
        this.clearButton.style.cursor = 'not-allowed';
        this.clearButton.style.pointerEvents = 'none';
      }

      this.hideContinueToEngravingButton();
    }

    enableAreaButtons() {
      if (this.confirmButton) {
        this.confirmButton.disabled = !this.isValidSize;
        this.confirmButton.style.opacity = this.isValidSize ? '1' : '0.5';
        this.confirmButton.style.cursor = this.isValidSize ? 'pointer' : 'not-allowed';
        this.confirmButton.style.pointerEvents = '';
      }

      if (this.clearButton) {
        this.clearButton.disabled = false;
        this.clearButton.style.opacity = '1';
        this.clearButton.style.cursor = 'pointer';
        this.clearButton.style.pointerEvents = '';
      }

      this.showContinueToEngravingButton();
    }

    showContinueToEngravingButton() {
      const continueButton = document.getElementById('continue-to-engraving');
      if (continueButton) {
        continueButton.style.display = 'inline-block';
        continueButton.disabled = false;
        continueButton.style.opacity = '1';
        continueButton.style.cursor = 'pointer';
        continueButton.style.pointerEvents = '';
      }
    }

    hideContinueToEngravingButton() {
      const continueButton = document.getElementById('continue-to-engraving');
      if (continueButton) {
        continueButton.style.display = 'none';
        continueButton.disabled = true;
        continueButton.style.opacity = '0.5';
        continueButton.style.cursor = 'not-allowed';
        continueButton.style.pointerEvents = 'none';
      }
    }

    disableZoomButtons() {
      // Find and disable Mapbox zoom control buttons
      const mapContainer = this.mapContainer;
      if (!mapContainer) return;
      
      // Find zoom in and zoom out buttons in the navigation control
      const zoomInButton = mapContainer.querySelector('.mapboxgl-ctrl-zoom-in');
      const zoomOutButton = mapContainer.querySelector('.mapboxgl-ctrl-zoom-out');
      
      if (zoomInButton) {
        zoomInButton.disabled = true;
        zoomInButton.style.opacity = '0.5';
        zoomInButton.style.cursor = 'not-allowed';
        zoomInButton.style.pointerEvents = 'none';
      }
      
      if (zoomOutButton) {
        zoomOutButton.disabled = true;
        zoomOutButton.style.opacity = '0.5';
        zoomOutButton.style.cursor = 'not-allowed';
        zoomOutButton.style.pointerEvents = 'none';
      }
      
    }

    enableZoomButtons() {
      // Find and re-enable Mapbox zoom control buttons
      const mapContainer = this.mapContainer;
      if (!mapContainer) return;
      
      // Find zoom in and zoom out buttons in the navigation control
      const zoomInButton = mapContainer.querySelector('.mapboxgl-ctrl-zoom-in');
      const zoomOutButton = mapContainer.querySelector('.mapboxgl-ctrl-zoom-out');
      
      if (zoomInButton) {
        zoomInButton.disabled = false;
        zoomInButton.style.opacity = '';
        zoomInButton.style.cursor = '';
        zoomInButton.style.pointerEvents = '';
      }
      
      if (zoomOutButton) {
        zoomOutButton.disabled = false;
        zoomOutButton.style.opacity = '';
        zoomOutButton.style.cursor = '';
        zoomOutButton.style.pointerEvents = '';
      }
      
    }



    changeArea() {
        
        // Clear previous preview job ID and cached GLB
        this.currentPreviewJobId = null;
        this.previewJobPromise = null;
        this.activePreviewJobToken = null;
        this.cachedGLBUrl = null;
        this.cachedGLBCoordinates = null;
        this.finalJobId = null;
        this.finalGlbUrl = null;
        this.finalGlbCoordinates = null;
        this.finalEngravingText = null;
        this.resetFinalPreviewPolling();
        this.isGeneratingFinalGLB = false;
        this.isGeneratingPreviewGLB = false; // Reset preview generation flag
        this.setChangeAreaButtonEnabled(true); // Ensure button is enabled when changing area

        // Hide GLB preview container temporarily (keep continue button visible)
        const glbPreviewContainer = document.getElementById('glb-preview-container');
        if (glbPreviewContainer) {
          glbPreviewContainer.style.display = 'none';
      }

      // Clear any existing GLB viewer
      if (this.glbViewer) {
        this.glbViewer.destroy();
        this.glbViewer = null;
      }

      // Capture current rectangle coordinates from the map
      this.captureCurrentRectangleCoordinates();

      // Capture new map snapshot with updated coordinates
      if (this.currentSelectedArea) {
        this.captureMapSnapshot();
      }

      // Update the area display with new coordinates
      this.displaySelectedArea();

      // Update the selected area indicator position
      if (this.currentSelectedArea) {
        this.showSelectedAreaIndicator(); // This will update the position
        this.generateGLBPreview();
      } else {
        console.error('No area selected - cannot generate GLB preview');
      }

    }

    captureCurrentRectangleCoordinates() {
      if (!this.map) {
        console.error('Map not initialized');
        return;
      }

      try {
        // Get the rectangle overlay element from the map container parent
        const mapContainerParent = this.mapContainer.parentElement;
        const rectangle = mapContainerParent.querySelector('.rectangle-overlay');
        if (!rectangle) {
          console.error('Rectangle overlay not found in map container');
          return;
        }


        // Get rectangle dimensions and position
        const rectBounds = rectangle.getBoundingClientRect();
        const mapBounds = this.mapContainer.getBoundingClientRect();

        // Calculate rectangle position relative to map container
        const rectLeft = rectBounds.left - mapBounds.left;
        const rectTop = rectBounds.top - mapBounds.top;
        const rectRight = rectLeft + rectBounds.width;
        const rectBottom = rectTop + rectBounds.height;

        // Convert pixel coordinates to lat/lng
        const topRight = this.map.unproject([rectRight, rectTop]);
        const bottomLeft = this.map.unproject([rectLeft, rectBottom]);

        if (!MapUtils) {
          if (!this.mapUtilsMissingWarned) {
            console.warn('[CoordinateGrabberWidget] MapUtils module not loaded; unable to update rectangle coordinates.');
            this.mapUtilsMissingWarned = true;
          }
          return;
        }

        // Transform coordinates based on map rotation to get correct geographic bounding box
        const transformedCoords = MapUtils.transformCoordinatesForRotation(
          topRight.lat, topRight.lng, 
          bottomLeft.lat, bottomLeft.lng, 
          this.mapRotation
        );

        // Update the current selected area with transformed coordinates
        this.currentSelectedArea = {
          topRight: {
            lat: transformedCoords.topRight.lat,
            lng: transformedCoords.topRight.lng
          },
          bottomLeft: {
            lat: transformedCoords.bottomLeft.lat,
            lng: transformedCoords.bottomLeft.lng
          },
          rotation: this.mapRotation, // Store current rotation state
          timestamp: new Date().toISOString()
        };


      } catch (error) {
        console.error('Error capturing current rectangle coordinates:', error);
      }
    }

    captureMapSnapshot() {
      
      try {
        const canvas = this.map.getCanvas();
        
        // Check if this is a WebGL canvas
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        
        // Force a render to ensure the buffer is up to date
        this.map.triggerRepaint();
        
        // Try immediate capture first (synchronous)
        try {
          // Test if we can read from the canvas immediately
          const testData = canvas.toDataURL('image/png', 1.0);
          
          if (testData.length > 1000) {
            // Canvas is readable, proceed with cropping
            const isMobile = window.innerWidth <= 768;
            const rectWidth = isMobile ? 160 : 200;
            const rectHeight = isMobile ? 93 : 116;
            
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            const left = Math.max(0, centerX - rectWidth / 2);
            const top = Math.max(0, centerY - rectHeight / 2);
            
            
            // Create a new canvas for the cropped area
            const cropCanvas = document.createElement('canvas');
            cropCanvas.width = rectWidth;
            cropCanvas.height = rectHeight;
            const cropCtx = cropCanvas.getContext('2d');
            
            // Draw the specific area from the map canvas
            cropCtx.drawImage(
              canvas,
              left, top, rectWidth, rectHeight,  // source rectangle
              0, 0, rectWidth, rectHeight        // destination rectangle
            );
            
            // Convert to data URL
            this.mapSnapshot = cropCanvas.toDataURL('image/png', 1.0);
            
            return this.mapSnapshot;
          } else {
            console.warn('⚠️ Canvas appears to be blank, trying delayed capture...');
            
            // Try delayed capture
            setTimeout(() => {
              this.captureMapSnapshotDelayed();
            }, 200);
            
            return null;
          }
          
        } catch (immediateError) {
          console.error('❌ Immediate capture failed:', immediateError);
          this.mapSnapshot = null;
          return null;
        }
        
      } catch (error) {
        console.error('❌ Snapshot capture failed:', error);
        this.mapSnapshot = null;
        return null;
      }
    }
    
    captureMapSnapshotDelayed() {
      
      try {
        const canvas = this.map.getCanvas();
        const testData = canvas.toDataURL('image/png', 1.0);
        
        if (testData.length > 1000) {
          // Proceed with delayed capture
          const isMobile = window.innerWidth <= 768;
          const rectWidth = isMobile ? 160 : 200;
          const rectHeight = isMobile ? 93 : 116;
          
          const centerX = canvas.width / 2;
          const centerY = canvas.height / 2;
          const left = Math.max(0, centerX - rectWidth / 2);
          const top = Math.max(0, centerY - rectHeight / 2);
          
          const cropCanvas = document.createElement('canvas');
          cropCanvas.width = rectWidth;
          cropCanvas.height = rectHeight;
          const cropCtx = cropCanvas.getContext('2d');
          
          cropCtx.drawImage(
            canvas,
            left, top, rectWidth, rectHeight,
            0, 0, rectWidth, rectHeight
          );
          
          this.mapSnapshot = cropCanvas.toDataURL('image/png', 1.0);
        } else {
          console.error('❌ Delayed capture also failed - canvas still blank');
          this.mapSnapshot = null;
        }
      } catch (error) {
        console.error('❌ Delayed capture error:', error);
        this.mapSnapshot = null;
      }
    }






    displaySelectedArea() {
      if (!this.currentSelectedArea) return;

      const formatCoordinate = (coord, isLat = false) => {
        const direction = isLat ? (coord >= 0 ? 'N' : 'S') : (coord >= 0 ? 'E' : 'W');
        return `${Math.abs(coord).toFixed(6)}° ${direction}`;
      };

      const { topRight, bottomLeft } = this.currentSelectedArea;

      const areaText = `✓ Map Area Selected

Top-Right: ${formatCoordinate(topRight.lat, true)}, ${formatCoordinate(topRight.lng)}
Bottom-Left: ${formatCoordinate(bottomLeft.lat, true)}, ${formatCoordinate(bottomLeft.lng)}

Coordinates ready for use`;

      // Clear existing content safely
      this.areaDisplay.textContent = '';

      // Create paragraph element safely
      const paragraph = document.createElement('p');
      paragraph.textContent = areaText;
      this.areaDisplay.appendChild(paragraph);
      this.areaDisplay.classList.add('has-data');
    }

    controlShopifyAddToCart(status) {
      if (!ShopifyIntegration || typeof ShopifyIntegration.setupCartControls !== 'function') {
        console.warn('[CoordinateGrabberWidget] ShopifyIntegration module not loaded; skipping cart control setup.');
        return;
      }
      ShopifyIntegration.setupCartControls(this, status);
    }

    async addToCartWithAreaData(form, options = {}) {
      if (!ShopifyIntegration || typeof ShopifyIntegration.addToCartWithAreaData !== 'function') {
        console.error('[CoordinateGrabberWidget] ShopifyIntegration module not loaded; cannot add to cart.');
        alert('Unable to add to cart right now. Please try again later.');
        return;
      }
      await ShopifyIntegration.addToCartWithAreaData(this, form, options);
    }

    extractCartErrorMessage(payload) {
      if (ShopifyIntegration && typeof ShopifyIntegration.extractCartErrorMessage === 'function') {
        return ShopifyIntegration.extractCartErrorMessage(payload);
      }
      return null;
    }

    setButtonLoading(button, isLoading, loadingText = 'Adding...') {
      if (ShopifyIntegration && typeof ShopifyIntegration.setButtonLoading === 'function') {
        ShopifyIntegration.setButtonLoading(button, isLoading, loadingText);
      }
    }

    toggleFinalActionButtonsDisabled(disabled, excludeButton = null) {
      if (ShopifyIntegration && typeof ShopifyIntegration.toggleFinalActionButtonsDisabled === 'function') {
        ShopifyIntegration.toggleFinalActionButtonsDisabled(this, disabled, excludeButton);
      }
    }

    getOrientationName(rotation) {
      if (ShopifyIntegration && typeof ShopifyIntegration.getOrientationName === 'function') {
        return ShopifyIntegration.getOrientationName(rotation);
      }
      const names = { 0: 'North', 90: 'East', 180: 'South', 270: 'West' };
      return names[rotation] || 'North';
    }

    coordinatesMatch(coords1, coords2) {
      if (!coords1 || !coords2) return false;
      const tolerance = 0.000001;
      return Math.abs(coords1.topRight.lat - coords2.topRight.lat) < tolerance &&
             Math.abs(coords1.topRight.lng - coords2.topRight.lng) < tolerance &&
             Math.abs(coords1.bottomLeft.lat - coords2.bottomLeft.lat) < tolerance &&
             Math.abs(coords1.bottomLeft.lng - coords2.bottomLeft.lng) < tolerance &&
             ((coords1.rotation || 0) === (coords2.rotation || 0));
    }

    async resolveGlbUrlForJob(jobId, retries = 8, delayMs = 750) {
      if (!jobId) {
        return null;
      }

      const backendUrl = this.backendUrl;
      const glbUrl = `${backendUrl}/files/${jobId}/signed-glb`;

      for (let attempt = 0; attempt < retries; attempt++) {
        try {
          const response = await fetch(glbUrl, { cache: 'no-store' });
          if (response.ok) {
            const data = await response.json();
            if (data && data.signed_url) {
              return this.enforceHttpsUrl(data.signed_url);
            }
          } else {
            console.warn(`[CoordinateGrabberWidget] GLB signed URL not ready (status ${response.status}) - attempt ${attempt + 1}/${retries}`);
          }
        } catch (error) {
          console.error('[CoordinateGrabberWidget] Error fetching signed GLB URL:', error);
        }

        if (attempt < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }

      return null;
    }

    cloneSelectedArea(area) {
      if (!area) return null;
      return {
        topRight: { ...(area?.topRight || {}) },
        bottomLeft: { ...(area?.bottomLeft || {}) },
        rotation: area?.rotation || 0
      };
    }

    enforceHttpsUrl(url) {
      if (typeof url !== 'string' || url === '') {
        return url;
      }
      if (url.startsWith('//')) {
        return `https:${url}`;
      }
      if (/^http:\/\//i.test(url)) {
        return url.replace(/^http:\/\//i, 'https://');
      }
      return url;
    }

    resetFinalPreviewPolling() {
      if (this.finalPreviewRetryTimer) {
        clearTimeout(this.finalPreviewRetryTimer);
        this.finalPreviewRetryTimer = null;
      }
      this.finalPreviewRetryCount = 0;
    }

    scheduleFinalPreviewRetry() {
      if (this.finalPreviewRetryCount >= 6) {
        this.updateFinalPreviewStatus('Preview is taking longer than expected. Please try again in a moment or reselect your area.');
        return;
      }
      if (this.finalPreviewRetryTimer) {
        return;
      }
      this.finalPreviewRetryCount += 1;
      this.finalPreviewRetryTimer = setTimeout(() => {
        this.finalPreviewRetryTimer = null;
        this.generateFinalGLB();
      }, 1500);
    }

    startPreviewJob(payload, areaSnapshot, jobToken) {
      const backendUrl = this.backendUrl;

      const jobPromise = (async () => {
        const response = await fetch(`${backendUrl}/jobs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          // Handle 429 (Too Many Requests) specifically
          if (response.status === 429) {
            let errorMessage = 'Rate limit exceeded. Please try again in a few moments.';
            try {
              const errorData = await response.json();
              if (errorData.detail && errorData.detail.retry_after) {
                const retrySeconds = errorData.detail.retry_after;
                const retryMinutes = Math.ceil(retrySeconds / 60);
                errorMessage = `Too many requests. Please try again in ${retryMinutes} minute${retryMinutes !== 1 ? 's' : ''}.`;
              }
            } catch (e) {
              // If we can't parse the error response, use default message
            }
            const rateLimitError = new Error(`GLB generation rate limited: ${response.status}`);
            rateLimitError.status = 429;
            rateLimitError.userMessage = errorMessage;
            throw rateLimitError;
          }
          throw new Error(`GLB generation failed: ${response.status}`);
        }

        const data = await response.json();
        const jobId = data.job_id;

        if (this.activePreviewJobToken === jobToken) {
          this.currentPreviewJobId = jobId;
        }

        const resolvedGlbUrl = await this.resolveGlbUrlForJob(jobId, 12, 800);
        if (!resolvedGlbUrl) {
          throw new Error('GLB signed URL not ready');
        }

        const secureGlbUrl = this.enforceHttpsUrl(resolvedGlbUrl);
        return { jobId, glbUrl: secureGlbUrl, area: areaSnapshot };
      })();

      this.previewJobPromise = jobPromise
        .then(result => {
          const secureUrl = this.enforceHttpsUrl(result?.glbUrl);
          if (secureUrl && secureUrl !== result.glbUrl) {
            result.glbUrl = secureUrl;
          }
          if (
            this.activePreviewJobToken === jobToken &&
            this.currentSelectedArea &&
            this.coordinatesMatch(result.area, this.currentSelectedArea)
          ) {
            this.cachedGLBUrl = secureUrl || result.glbUrl;
            this.cachedGLBCoordinates = this.cloneSelectedArea(result.area);
          }
          return result;
        })
        .catch(error => {
          if (this.previewJobPromise === jobPromise) {
            this.previewJobPromise = null;
          }
          throw error;
        });

      return this.previewJobPromise;
    }

    async generateGLBPreview() {
      if (!this.currentSelectedArea) {
        console.error('No area selected for GLB preview');
        return;
      }
      
      // Prevent multiple simultaneous generation requests
      if (this.isGeneratingPreviewGLB) {
        console.log('[CoordinateWidget] GLB preview generation already in progress, ignoring duplicate request');
        return;
      }

      try {
        this.isGeneratingPreviewGLB = true;
        this.setChangeAreaButtonEnabled(false);
        
        const glbPreviewContainer = document.getElementById('glb-preview-container');
        if (glbPreviewContainer) {
          glbPreviewContainer.style.display = 'block';
        }

        const viewerAvailable = Boolean(GLBViewer) && this.webglSupported !== false;
        if (viewerAvailable && !this.glbViewer) {
          this.glbViewer = new GLBViewer('glb-viewer-canvas', 'glb-viewer-loading', {
            overlayId: 'glb-progress-overlay',
            fillId: null,
            textId: 'glb-progress-text'
          });
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        const viewerCanRender = this.glbViewer && this.glbViewer.isSupported !== false;

        if (viewerCanRender) {
          this.glbViewer.startProgress('Preparing 3D preview...');
        }

        this.updateGLBPreviewStatus('Generating 3D preview...');

        const areaSnapshot = this.cloneSelectedArea(this.currentSelectedArea);
        const payload = {
          bbox: {
            tr: [areaSnapshot.topRight.lat, areaSnapshot.topRight.lng],
            bl: [areaSnapshot.bottomLeft.lat, areaSnapshot.bottomLeft.lng]
          },
          rotation: areaSnapshot.rotation || 0,
          glb_only: true
        };

        const jobToken = Symbol('preview-job');
        this.activePreviewJobToken = jobToken;

        if (viewerCanRender) {
          this.glbViewer.updateProgress(35, 'Preparing 3D model...');
        }

        let previewResult;
        try {
          previewResult = await this.startPreviewJob(payload, areaSnapshot, jobToken);
        } catch (error) {
          throw error;
        }

        if (!previewResult || !previewResult.glbUrl) {
          throw new Error('GLB preview job completed without a GLB URL');
        }

        const previewGlbUrl = this.enforceHttpsUrl(previewResult.glbUrl);

        if (viewerCanRender) {
          this.glbViewer.updateProgress(55, 'Loading 3D model...');
          try {
            await this.glbViewer.loadGLB(previewGlbUrl);
            const overlayText = this.placeName ? this.placeName.toUpperCase() : '';
            if (typeof this.glbViewer.setEngravingText === 'function') {
              this.glbViewer.setEngravingText(overlayText);
            }
            this.glbViewer.completeProgress('Preview ready!');
          } catch (loadError) {
            // Silently ignore cancellation errors (user changed area or viewer was destroyed)
            if (loadError && loadError.message && (
              loadError.message.includes('cancelled') || 
              loadError.message.includes('Viewer destroyed')
            )) {
              console.log('[CoordinateWidget] GLB load cancelled - ignoring');
              return; // Exit early, don't show error
            }
            throw loadError; // Re-throw other errors
          }
        }

        this.updateGLBPreviewStatus(
          viewerCanRender
            ? '3D preview ready! Drag to rotate. Pinch/scroll to zoom.'
            : '3D preview generated. Interactive 3D not available on this device.'
        );
      } catch (error) {
        // Ignore cancellation errors
        if (error && error.message && (
          error.message.includes('cancelled') || 
          error.message.includes('Viewer destroyed')
        )) {
          console.log('[CoordinateWidget] Preview generation cancelled - ignoring');
          this.isGeneratingPreviewGLB = false;
          this.setChangeAreaButtonEnabled(true);
          return;
        }
        
        console.error('GLB preview generation failed:', error);
        
        // Use user-friendly message for rate limiting, otherwise use default
        const errorMessage = (error && error.userMessage) 
          ? error.userMessage 
          : 'Error generating preview. Please try again.';
        
        this.updateGLBPreviewStatus(errorMessage);
        if (this.glbViewer && this.glbViewer.isSupported !== false) {
          this.glbViewer.failProgress('Preview unavailable');
        }
      } finally {
        this.isGeneratingPreviewGLB = false;
        this.setChangeAreaButtonEnabled(true);
      }
    }

    updateGLBPreviewStatus(message) {
      const statusEl = document.getElementById('glb-preview-status');
      if (statusEl) {
        statusEl.textContent = message;
      }
    }

    setChangeAreaButtonEnabled(enabled) {
      if (this.clearButton) {
        this.clearButton.disabled = !enabled;
        if (enabled) {
          this.clearButton.classList.remove('disabled');
          this.clearButton.style.opacity = '1';
          this.clearButton.style.cursor = 'pointer';
        } else {
          this.clearButton.classList.add('disabled');
          this.clearButton.style.opacity = '0.6';
          this.clearButton.style.cursor = 'not-allowed';
        }
      }
    }

    async generateFinalGLB() {
      if (!this.currentSelectedArea || !this.placeName) {
        console.error('Missing coordinates or place name for final GLB generation');
        return;
      }
      if (this.isGeneratingFinalGLB) {
        return;
      }

      this.isGeneratingFinalGLB = true;
      const engraving = this.placeName.trim().toUpperCase();

      try {
        const viewerAvailable = Boolean(GLBViewer) && this.webglSupported !== false;
        if (viewerAvailable && !this.glbViewerFinal) {
          this.glbViewerFinal = new GLBViewer('glb-viewer-canvas-final', 'glb-viewer-loading-final', {
            overlayId: 'glb-progress-overlay-final',
            fillId: null,
            textId: 'glb-progress-text-final'
          });
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        const finalViewerSupportsWebGL = this.glbViewerFinal && this.glbViewerFinal.isSupported !== false;

        let cachedUrl = null;
        if (this.cachedGLBUrl && this.cachedGLBCoordinates && this.coordinatesMatch(this.cachedGLBCoordinates, this.currentSelectedArea)) {
          cachedUrl = this.enforceHttpsUrl(this.cachedGLBUrl);
        } else if (this.__landmarkFileUrls && this.__landmarkFileUrls.preview_url) {
          cachedUrl = this.enforceHttpsUrl(this.__landmarkFileUrls.preview_url);
        }

        if (!cachedUrl && this.previewJobPromise) {
          this.updateFinalPreviewStatus('Finishing up your preview...');
          try {
            const previewResult = await this.previewJobPromise;
            if (previewResult && previewResult.glbUrl && this.coordinatesMatch(previewResult.area, this.currentSelectedArea)) {
              const securePreviewUrl = this.enforceHttpsUrl(previewResult.glbUrl);
              cachedUrl = securePreviewUrl;
              this.cachedGLBUrl = securePreviewUrl;
              this.cachedGLBCoordinates = this.cloneSelectedArea(previewResult.area);
            }
          } catch (error) {
            console.error('Waiting for preview job to finish failed:', error);
          }
        }

        if (!cachedUrl && this.currentPreviewJobId) {
          this.updateFinalPreviewStatus('Finishing up your preview...');
          const resolvedUrl = await this.resolveGlbUrlForJob(this.currentPreviewJobId, 12, 800);
          if (resolvedUrl) {
            cachedUrl = this.enforceHttpsUrl(resolvedUrl);
            this.cachedGLBUrl = cachedUrl;
            this.cachedGLBCoordinates = this.cloneSelectedArea(this.currentSelectedArea);
          }
        }

        if (!cachedUrl && !this.previewJobPromise && !this.currentPreviewJobId) {
          this.updateFinalPreviewStatus('Preview could not be generated. Please reselect your area.');
          if (this.glbViewerFinal && finalViewerSupportsWebGL) {
            this.glbViewerFinal.failProgress('Preview unavailable');
          }
          return;
        }

        if (!cachedUrl) {
          this.updateFinalPreviewStatus('Preview is still generating. Please wait a moment and try again.');
          if (this.glbViewerFinal && finalViewerSupportsWebGL) {
            this.glbViewerFinal.failProgress('Preview unavailable');
          }
          this.scheduleFinalPreviewRetry();
          return;
        }

        cachedUrl = this.enforceHttpsUrl(cachedUrl);

        this.resetFinalPreviewPolling();
        this.updateFinalPreviewStatus(finalViewerSupportsWebGL ? 'Loading 3D preview...' : 'Preparing downloadable preview...');

        if (this.glbViewerFinal && finalViewerSupportsWebGL) {
          this.glbViewerFinal.startProgress('Loading 3D model...');
          await this.glbViewerFinal.loadGLB(cachedUrl);
          if (typeof this.glbViewerFinal.setEngravingText === 'function') {
            this.glbViewerFinal.setEngravingText(engraving);
          }
          this.glbViewerFinal.completeProgress('Preview ready!');
        } else if (!finalViewerSupportsWebGL && viewerAvailable) {
          console.warn('[CoordinateGrabberWidget] WebGL not available for final preview; skipping live render.');
        }

        this.finalGlbUrl = cachedUrl;
        this.finalGlbCoordinates = this.cloneSelectedArea(this.currentSelectedArea);
        this.finalEngravingText = engraving;
        this.updateFinalPreviewStatus(
          finalViewerSupportsWebGL
            ? '3D preview ready! Drag to rotate. Pinch/scroll to zoom.'
            : 'Preview generated. Interactive 3D not available on this device.'
        );
      } catch (error) {
        console.error('Final GLB generation failed:', error);
        this.updateFinalPreviewStatus('Error generating final preview. Please try again.');
        if (this.glbViewerFinal && this.glbViewerFinal.isSupported !== false) {
          this.glbViewerFinal.failProgress('Final preview unavailable');
        }
        this.scheduleFinalPreviewRetry();
      } finally {
        this.isGeneratingFinalGLB = false;
      }
    }

    updateFinalPreviewStatus(message) {
      const statusEl = document.getElementById('topomap-preview-status');
      if (statusEl) {
        statusEl.textContent = message;
      }
    }

    // STL generation removed - GLB-only pipeline
    // The GLB file serves as both preview and final output


    // Selected Area Indicator Methods
    createSelectedAreaIndicator() {
      if (this.selectedAreaIndicator) {
        this.hideSelectedAreaIndicator();
      }

      // Create the indicator rectangle
      this.selectedAreaIndicator = document.createElement('div');
      this.selectedAreaIndicator.className = 'selected-area-indicator';

      // Create the label
      this.selectedAreaLabel = document.createElement('div');
      this.selectedAreaLabel.className = 'selected-area-label';
      this.selectedAreaLabel.textContent = 'Selected Area';

      // Attach label to indicator
      this.selectedAreaIndicator.appendChild(this.selectedAreaLabel);

    }

    showSelectedAreaIndicator() {
      if (!this.currentSelectedArea || !this.map) {
        console.warn('Cannot show selected area indicator: no area selected or map not initialized');
        return;
      }

      // Create indicator if it doesn't exist
      if (!this.selectedAreaIndicator) {
        this.createSelectedAreaIndicator();
      }

      // Add to map container parent (same as rectangle overlay)
      const mapContainerParent = this.mapContainer.parentElement;
      if (mapContainerParent && !mapContainerParent.contains(this.selectedAreaIndicator)) {
        mapContainerParent.appendChild(this.selectedAreaIndicator);
      }

      // Position the indicator at the selected coordinates
      this.updateSelectedAreaIndicatorPosition();

    }

    hideSelectedAreaIndicator() {
      if (this.selectedAreaIndicator && this.selectedAreaIndicator.parentElement) {
        this.selectedAreaIndicator.parentElement.removeChild(this.selectedAreaIndicator);
      }
      this.selectedAreaIndicator = null;
      this.selectedAreaLabel = null;
    }

    updateSelectedAreaIndicatorPosition() {
      if (!this.selectedAreaIndicator || !this.currentSelectedArea || !this.map) {
        return;
      }

      try {
        // Get the selected coordinates (geographic)
        const { topRight, bottomLeft, rotation: storedRotation } = this.currentSelectedArea;
        const currentRotation = this.map.getBearing();

        // If we have stored rotation info, we need to handle coordinate transformation
        // The stored coordinates are already transformed for the rotation at selection time
        // We need to project them directly without additional transformation
        
        // Project the stored geographic coordinates to current screen coordinates
        const topRightScreen = this.map.project([topRight.lng, topRight.lat]);
        const bottomLeftScreen = this.map.project([bottomLeft.lng, bottomLeft.lat]);

        // Calculate all four corners to handle any rotation scenario
        const topLeftScreen = this.map.project([bottomLeft.lng, topRight.lat]);
        const bottomRightScreen = this.map.project([topRight.lng, bottomLeft.lat]);

        // Find the actual bounding box of all projected points
        const allX = [topRightScreen.x, bottomLeftScreen.x, topLeftScreen.x, bottomRightScreen.x];
        const allY = [topRightScreen.y, bottomLeftScreen.y, topLeftScreen.y, bottomRightScreen.y];

        const left = Math.min(...allX);
        const right = Math.max(...allX);
        const top = Math.min(...allY);
        const bottom = Math.max(...allY);

        const width = Math.abs(right - left);
        const height = Math.abs(bottom - top);
        const centerX = left + width / 2;
        const centerY = top + height / 2;

        // Ensure minimum size to prevent invisible rectangles
        const minSize = 10;
        const finalWidth = Math.max(width, minSize);
        const finalHeight = Math.max(height, minSize);

        // Position and size the indicator based on actual geographic bounds
        this.selectedAreaIndicator.style.left = `${centerX}px`;
        this.selectedAreaIndicator.style.top = `${centerY}px`;
        this.selectedAreaIndicator.style.width = `${finalWidth}px`;
        this.selectedAreaIndicator.style.height = `${finalHeight}px`;
        this.selectedAreaIndicator.style.transform = 'translate(-50%, -50%)';

        // Ensure it's visible
        this.selectedAreaIndicator.style.display = 'block';

        // Debug logging to track coordinate behavior

      } catch (error) {
        console.error('Error updating selected area indicator position:', error);
      }
    }

    updateSelectedAreaIndicator() {
      if (this.selectedAreaIndicator && this.currentSelectedArea) {
        this.updateSelectedAreaIndicatorPosition();
      }
    }
  }

  // Initialize when DOM is ready
  function initializeWidget() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        const widget = new CoordinateGrabberWidget();
        window.coordinateGrabberWidget = widget;
      });
    } else {
      const widget = new CoordinateGrabberWidget();
      window.coordinateGrabberWidget = widget;
    }
  }

  window.Landmark = window.Landmark || {};
  window.Landmark.CoordinateWidget = {
    init: initializeWidget,
    createInstance: () => new CoordinateGrabberWidget()
  };

  // Always expose manual initializer for integrations doing manual bootstrapping
  window.initializeCoordinateGrabberWidget = initializeWidget;

  // Auto-initialize unless explicitly disabled
  if (config.autoInit !== false) {
    initializeWidget();
  }

})();
